
import { CSSProperties, useEffect, useRef, useState } from "react";
import styles from "./Autocomplete.module.css";
import IconSelect from "../../icons/IconSelect";
import Flex from "../../Flex/Flex";
import IconSearch from "../../icons/IconSearch";
import { clamp } from "@/utils/dateUtils";
import Flyout from "@/components/Flyout";

const ignoredKeys = new Set([
  'Control', 'Shift', 'Alt', 'Meta', 'ContextMenu', 'F1',
  'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10',
  'F11', 'F12', 'F13', 'F14', 'F15', 'F16', 'F17', 'F18',
  'PageUp', 'PageDown', 'Insert', 'End', 'Home', 'Escape',
]);

export interface AutocompleteProps<T> {
  optionRender?: (option: T, index: number) => React.ReactNode; // options in the dropdown, before filtering by the search string

  value?: T;
  onChange?: (option: T, index: number) => void;
  onTextChange?: (text: string) => void;       // reports the text in the box, minus the prefix.
  onCreateNewOption?: (value: string) => boolean|void; // this only really makes sense if T === 'string'. Otherwise, how do we make a new thing based on the text you type?

  inputId?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;

  placeholder?: string;

  options?: T[];
  defaultOption?: T;
  getFilterProperty?: (option: T) => string; // you can set this to always return an empty string to effectively disable the filtering behavior, otherwise, how to get the string value which is compared against the text box contents
  optionToDisplayString?: (option: T) => string; // when a value of type T is set, how do we convert that to the text in the input box? default just call 'toString()'

  fullWidth?: boolean;
  showSearchIcon?: boolean;
  prefix?: string;
  
  type?: "text" | "number"; //We can pass this in to allow a user to type valid floats into the autocomplete input.

  style?: CSSProperties;
}

export default function Autocomplete<T>(props: AutocompleteProps<T>) {
  const {
    optionRender,
    value,
    onChange,
    onTextChange, // this does not include the 'prefix' - fires everytime the text box changes.
    onCreateNewOption,
    inputId,
    defaultOption,
    getFilterProperty,
    optionToDisplayString,
    loading = false,
    disabled = false,
    invalid = false,
    placeholder = null,
    options = [],
    fullWidth = false,
    showSearchIcon = false,
    prefix = "",
    type = "text",
    style = {},
    ...rest
  } = props;
  
  const _optionRender = optionRender ?? ((option: T, index: number): React.ReactNode => {
    return option.toString();
  });
  const _getFilterProperty = getFilterProperty ?? ((option: T) => option.toString());
  const _optionToDisplayString = optionToDisplayString ?? ((option: T) => option.toString());

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>(prefix);
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number>(0);
  const dropdownRef = useRef();
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    setFilteredOptions(options);
    setHoveredItemIndex(0);
  }, [options]);

  useEffect(() => {
    if (value && _optionToDisplayString(value)) {
      setText(prefix + _optionToDisplayString(value));
    }
  }, [value]);

  const clickHandler = (e: MouseEvent) => {
    if (e.target !== dropdownRef.current) {
      setHoveredItemIndex(0);
      setDropdownOpen(false);
    }
  };

  let displayOption = value;
  if (!displayOption && defaultOption) displayOption = defaultOption;

  useEffect(() => {
    if (window) {
      window.addEventListener("click", clickHandler);
      return () => window.removeEventListener("click", clickHandler);
    }
  }, []);

  return (
    <div
      className={`${styles.autocomplete} ${fullWidth ? styles.fullWidth : ''} ${invalid ? styles.autocompleteInvalid : ''}`}
      style={{...style}}
      onClick={(e) => {
        e.stopPropagation(); // so the event listener above doesn't fire.
        requestAnimationFrame(() => inputRef.current?.focus());
        setDropdownOpen(!dropdownOpen);
      }}
      {...rest}
    >
      <Flex
        justify="space-between"
        align="stretch"
        style={{minHeight:"2rem"}}
      >
        {showSearchIcon && (
          <div className={styles.iconSearch}>
            <IconSearch/>
          </div>
        )}
        <input
          id={inputId}
          className={styles.input}
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          ref={inputRef}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              setHoveredItemIndex(clamp(hoveredItemIndex + 1, 0, filteredOptions.length-1));
            } else if (e.key === "ArrowUp") {
              setHoveredItemIndex(clamp(hoveredItemIndex - 1, 0, filteredOptions.length-1));
            } else if (e.key === "Enter") {
              const option: T = filteredOptions[hoveredItemIndex];
              if (option === undefined) {
                // we hit enter, and the thing we typed doesn't match any existing option. create a new one.
                if(onCreateNewOption?.(text.trim().substring(prefix.length))) {
                  setText("");
                }
                setDropdownOpen(false);
                e.preventDefault();
                return;
              }

              setDropdownOpen(false);
              setText(prefix + _optionToDisplayString(option));
              onChange?.(option, options.findIndex(o => o === option));
              e.preventDefault();
            } else if (e.key === "Escape") {
              setDropdownOpen(false);
              setHoveredItemIndex(0);
            } else if (!ignoredKeys.has(e.key)) {
              setDropdownOpen(true);
            }
          }}
          value={text}
          onChange={(e) => {
            if (e.target.value && e.target.value !== "") {
              const trimmed = e.target.value.trim().substring(prefix.length).toLowerCase();
              setFilteredOptions(options.filter(option => {
                const a = _getFilterProperty(option).trim().toLowerCase();
                return !a || a === "" || a.indexOf(trimmed) !== -1;
              }));
            } else {
              setFilteredOptions(options);    
            }

            let value = e.target.value;
            if (!value.startsWith(prefix)) {
              value = prefix + value;
            }

            if (type === "number") {
              // This allows a number to be "34" | "34." | "34.4" | "34.43" | ".43" | ""
              // This does NOT allow "3asdf$%" | "4.4.4"
              const isValidNumber = value.replace(prefix,"").match(/^\d*(\d\.|\.\d)?\d*$/)?.length > 0;
              if (isValidNumber) {
                onTextChange?.(value.substring(prefix.length));
                setText(value);
              }
            } else {
              onTextChange?.(value.substring(prefix.length));
              setText(value);
            }
          }}
          onBlur={(_) => {
            // if we have text in the input box, that means we are partly-through creating a new option.
            // request to create one (if it's a new one). @TODO perhaps this should be optional...
            // because it's always just a 'request' to create a new one, the caller is in control of whether we do. *shrug*
            // the other way to explicitly request creating a new option is hitting 'enter'.
            // @NOTE(michaelp): In CreateNewBoxButton, onCreateNewOption is actually used to clear the input for invalid inputs.
            if (text && text.trim() !== prefix) {
              const option = options.find(o => (prefix + _optionToDisplayString(o)).trim() === text.trim());
              if (!option) {
                if(onCreateNewOption?.(text.trim().substring(prefix.length))) {
                  setText("");
                }
              }
            }
          }}
        />
        <Flex className={styles.iconSelect} align="center">
          <IconSelect />
        </Flex>
      </Flex>
      {(dropdownOpen && filteredOptions.length > 0) && (
        <Flyout className={styles.dropdown} ref={dropdownRef}>
          {filteredOptions.map((option, index) => (
            <div 
              className={`${styles.dropdownItem} ${hoveredItemIndex === index ? styles.dropdownItemKeyboardHovered : ''}`}
              key={index}
              onClick={e => {
                setDropdownOpen(false);
                setText(prefix + _optionToDisplayString(option));
                onChange?.(option, index);
                e.stopPropagation(); // so we actually close the dropdown
                e.preventDefault();
              }}
            >
              {_optionRender(option, index)}
            </div>
          ))}
        </Flyout>
      )}
    </div>
  );
}
